import { z } from 'zod';

export const COMMISSION_RATE = 0.10; // 10%
export const COMMISSION_CAP = 500.00; // $500 maximum

export const CommissionInputSchema = z.object({
  itemPrice: z.number().min(0),
  shippingCost: z.number().min(0).default(0),
  // If true, shipping cost is added to application fee (platform pays Shippo)
  // If false, shipping goes to seller (no Shippo label)
  platformPaysShipping: z.boolean().default(false),
  // Effective commission rate for THIS seller. Defaults to the flat platform
  // rate; a SELLER_FEE_GRANTS award lowers it. Capped at COMMISSION_RATE so a
  // bad grant can never charge a seller more than the standard fee.
  commissionRate: z.number().min(0).max(COMMISSION_RATE).default(COMMISSION_RATE),
});

export const CommissionResultSchema = z.object({
  itemPrice: z.number(),
  shippingCost: z.number(),
  totalCharge: z.number(),
  commissionAmount: z.number(),
  // Realized rate AFTER the $500 cap — on a $10k sale this is 5%, not the 10%
  // that was asked for. Kept as-is for every existing caller.
  commissionRate: z.number(),
  // The rate this calculation was ASKED to apply, before the cap. This is what
  // tells you whether a fee grant was in play.
  appliedRate: z.number(),
  netToSeller: z.number(),
  applicationFeeAmount: z.number().int(), // cents for Stripe
  platformPaysShipping: z.boolean(),
});

export type CommissionResult = z.infer<typeof CommissionResultSchema>;

/**
 * `z.input` rather than `z.infer`: the schema's `.default()`s make those fields
 * optional for CALLERS, while `z.infer` would describe the post-parse shape and
 * force every existing call site to pass a rate it does not care about.
 */
export function calculateCommission(input: z.input<typeof CommissionInputSchema>): CommissionResult {
  const validated = CommissionInputSchema.parse(input);
  const { itemPrice, shippingCost, platformPaysShipping, commissionRate: rate } = validated;

  const totalCharge = itemPrice + shippingCost;

  // Commission is a percentage of item price only (not shipping).
  // Match PHP: round($cost * 0.1, 2); cap at $500. The rate is COMMISSION_RATE
  // unless the seller holds an active fee grant, which lowers it.
  let commissionAmount = Math.round(itemPrice * rate * 100) / 100;

  if (commissionAmount > COMMISSION_CAP) {
    commissionAmount = COMMISSION_CAP;
  }

  const commissionRate = itemPrice > 0 ? commissionAmount / itemPrice : 0;

  // Application fee depends on shipping handling
  let applicationFee = commissionAmount;
  if (platformPaysShipping) {
    // Platform pays Shippo, so keep shipping cost too
    applicationFee = commissionAmount + shippingCost;
  }
  // Otherwise, seller keeps shipping (no Shippo label)

  const applicationFeeAmount = Math.round(applicationFee * 100);

  // Net to seller: what they receive after Stripe transfer
  const netToSeller = totalCharge - applicationFee;

  return {
    itemPrice,
    shippingCost,
    totalCharge,
    commissionAmount,
    commissionRate,
    appliedRate: rate,
    netToSeller,
    applicationFeeAmount,
    platformPaysShipping,
  };
}
